import React from "react";
import {
  Pen, TrendingUp, Globe, Code2, Crown, Gift, Eye,
} from "lucide-react";

/**
 * Tooltip used by the client/editor sidebars (desktop only).
 * Mirrors Layout.DesktopSidebar style.
 */
function SidebarTip({ label, children }) {
  return (
    <div className="relative group/tip">
      {children}
      <span className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2.5 py-1.5 bg-[#1A1A1A] border border-white/10 text-white text-[11px] font-semibold rounded-lg whitespace-nowrap shadow-xl opacity-0 translate-x-1 group-hover/tip:opacity-100 group-hover/tip:translate-x-0 transition-all duration-150 z-[60]">
        {label}
      </span>
    </div>
  );
}

/**
 * Client navigation — Editor / Estadísticas / Dominio / Scripts / Plan / Referidos.
 * RESPONSIVE:
 *  - Desktop (md+): vertical 72px sidebar on the left.
 *  - Mobile (<md): bottom nav bar (fixed) so the canvas / tab content
 *    keeps the full viewport.
 */
export const NAV_ITEMS = [
  { id: "edit",      label: "Editor",       Icon: Pen },
  { id: "analytics", label: "Stats",        Icon: TrendingUp },
  { id: "domain",    label: "Dominio",      Icon: Globe },
  { id: "scripts",   label: "Scripts",      Icon: Code2 },
  { id: "sub",       label: "Plan",         Icon: Crown },
  { id: "referral",  label: "Refs",         Icon: Gift },
];

export function ClientNavSidebar({ activeTab, onNavigate, previewUrl }) {
  return (
    <>
      {/* ── Desktop vertical sidebar ─────────────────────────────────── */}
      <aside
        className="hidden md:flex w-[72px] flex-shrink-0 flex-col items-center py-5 border-r"
        style={{ background: "#0a0a0a", borderColor: "rgba(255,255,255,0.06)" }}
        data-testid="client-nav-sidebar"
      >
        {/* Logo pill — always at the top-left */}
        <div className="mb-5">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden shadow-xl"
            style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.10)" }}
          >
            <img src="/logo.png" alt="Sitios YA" className="w-10 h-10 object-contain" />
          </div>
        </div>

        <div className="w-8 h-px mb-3" style={{ background: "rgba(255,255,255,0.08)" }} />

        <nav className="flex flex-col gap-1 flex-1">
          {NAV_ITEMS.map(({ id, label, Icon }) => {
            const active = activeTab === id;
            return (
              <SidebarTip key={id} label={label}>
                <button
                  onClick={() => onNavigate(id)}
                  data-testid={`client-nav-${id}`}
                  className={`flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-150 ${
                    active
                      ? "text-[#FFC107] bg-[#FFC107]/[0.12]"
                      : "text-gray-500 hover:text-white hover:bg-white/[0.08]"
                  }`}
                >
                  <Icon size={19} />
                </button>
              </SidebarTip>
            );
          })}
        </nav>

        {previewUrl && (
          <>
            <div className="w-8 h-px mb-3" style={{ background: "rgba(255,255,255,0.08)" }} />
            <SidebarTip label="Ver sitio público">
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="client-nav-preview"
                className="flex items-center justify-center w-10 h-10 rounded-xl text-gray-500 hover:text-emerald-300 hover:bg-emerald-500/10 transition-all duration-150"
              >
                <Eye size={19} />
              </a>
            </SidebarTip>
          </>
        )}
      </aside>

      {/* ── Mobile bottom nav bar ────────────────────────────────────── */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 flex items-stretch border-t"
        style={{
          background: "#0a0a0a",
          borderColor: "rgba(255,255,255,0.08)",
          paddingBottom: "env(safe-area-inset-bottom, 0)",
        }}
        data-testid="client-nav-mobile"
      >
        {NAV_ITEMS.map(({ id, label, Icon }) => {
          const active = activeTab === id;
          return (
            <button
              key={id}
              onClick={() => onNavigate(id)}
              data-testid={`client-nav-mobile-${id}`}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2 transition-all duration-150 ${
                active
                  ? "text-[#FFC107]"
                  : "text-gray-500 active:text-white active:bg-white/[0.06]"
              }`}
            >
              <Icon size={18} strokeWidth={active ? 2.5 : 2} />
              <span className="text-[9.5px] font-bold leading-none tracking-tight">{label}</span>
            </button>
          );
        })}
      </nav>
    </>
  );
}
